
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../dashboard/components/Navbar';
import BottomNav from '../dashboard/components/BottomNav';

interface Transaction {
  id: string;
  type: 'sent' | 'received';
  amount: number;
  currency: string;
  receiverName: string;
  senderName: string;
  country: string;
  status: 'completed' | 'pending' | 'rejected';
  date: string;
  commission: number;
  reference: string;
}

export default function History() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'sent' | 'received'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'pending' | 'rejected'>('all');

  // بيانات تجريبية للتحويلات
  const transactions: Transaction[] = [
    {
      id: '1',
      type: 'sent',
      amount: 1000,
      currency: 'USD',
      receiverName: 'أحمد محمد',
      senderName: 'محمد علي',
      country: 'الإمارات العربية المتحدة',
      status: 'completed',
      date: '2024-01-15',
      commission: 25,
      reference: 'TXN001234567'
    },
    {
      id: '2',
      type: 'received',
      amount: 500,
      currency: 'EUR',
      receiverName: 'سارة أحمد',
      senderName: 'فاطمة حسن',
      country: 'المملكة العربية السعودية',
      status: 'pending',
      date: '2024-01-14',
      commission: 12.5,
      reference: 'TXN001234568'
    },
    {
      id: '3',
      type: 'sent',
      amount: 750,
      currency: 'USD',
      receiverName: 'خالد يوسف',
      senderName: 'محمد علي',
      country: 'الكويت',
      status: 'completed',
      date: '2024-01-13',
      commission: 18.75,
      reference: 'TXN001234569'
    },
    {
      id: '4',
      type: 'sent',
      amount: 300,
      currency: 'SAR',
      receiverName: 'نور الدين',
      senderName: 'محمد علي',
      country: 'قطر',
      status: 'rejected',
      date: '2024-01-12',
      commission: 7.5,
      reference: 'TXN001234570'
    },
    {
      id: '5',
      type: 'received',
      amount: 1200,
      currency: 'USD',
      receiverName: 'محمد علي',
      senderName: 'عبدالله أحمد',
      country: 'البحرين',
      status: 'completed',
      date: '2024-01-11',
      commission: 30,
      reference: 'TXN001234571'
    }
  ];

  const filteredTransactions = transactions.filter(transaction => {
    const typeMatch = filter === 'all' || transaction.type === filter;
    const statusMatch = statusFilter === 'all' || transaction.status === statusFilter;
    return typeMatch && statusMatch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'rejected':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'مكتمل';
      case 'pending':
        return 'قيد المعالجة';
      case 'rejected':
        return 'مرفوض';
      default:
        return 'غير محدد';
    }
  };

  const getTypeIcon = (type: string) => {
    return type === 'sent' ? 'ri-arrow-up-line' : 'ri-arrow-down-line';
  };

  const getTypeColor = (type: string) => {
    return type === 'sent' ? 'text-red-600' : 'text-green-600';
  };

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <Navbar />
      
      <div className="pt-20 pb-20 px-4">
        <div className="max-w-md mx-auto">
          {/* العنوان */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">سجل التحويلات</h1>
            <p className="text-gray-600 mt-2">جميع عمليات التحويل السابقة</p>
          </div>

          {/* فلاتر */}
          <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
            <div className="space-y-4">
              {/* فلتر النوع */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">نوع التحويل</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setFilter('all')}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      filter === 'all' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    الكل
                  </button>
                  <button
                    onClick={() => setFilter('sent')}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      filter === 'sent' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    مرسل
                  </button>
                  <button
                    onClick={() => setFilter('received')}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      filter === 'received' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    مستلم
                  </button>
                </div>
              </div>

              {/* فلتر الحالة */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">الحالة</label>
                <div className="grid grid-cols-4 gap-2">
                  <button
                    onClick={() => setStatusFilter('all')}
                    className={`py-2 px-2 rounded-lg text-xs font-medium transition-colors ${
                      statusFilter === 'all' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    الكل
                  </button>
                  <button
                    onClick={() => setStatusFilter('completed')}
                    className={`py-2 px-2 rounded-lg text-xs font-medium transition-colors ${
                      statusFilter === 'completed' 
                        ? 'bg-green-600 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    مكتمل
                  </button>
                  <button
                    onClick={() => setStatusFilter('pending')}
                    className={`py-2 px-2 rounded-lg text-xs font-medium transition-colors ${
                      statusFilter === 'pending' 
                        ? 'bg-yellow-600 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    معلق
                  </button>
                  <button
                    onClick={() => setStatusFilter('rejected')}
                    className={`py-2 px-2 rounded-lg text-xs font-medium transition-colors ${
                      statusFilter === 'rejected' 
                        ? 'bg-red-600 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    مرفوض
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* قائمة التحويلات */}
          <div className="space-y-4">
            {filteredTransactions.length === 0 ? (
              <div className="bg-white rounded-xl p-8 shadow-sm text-center">
                <i className="ri-file-list-line text-4xl text-gray-400 mb-4"></i>
                <h3 className="text-lg font-medium text-gray-600 mb-2">لا توجد تحويلات</h3>
                <p className="text-gray-500 text-sm">لم يتم العثور على تحويلات تطابق الفلاتر المحددة</p>
              </div>
            ) : (
              filteredTransactions.map((transaction) => (
                <div key={transaction.id} className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        transaction.type === 'sent' ? 'bg-red-100' : 'bg-green-100'
                      }`}>
                        <i className={`${getTypeIcon(transaction.type)} ${getTypeColor(transaction.type)}`}></i>
                      </div>
                      <div className="mr-3">
                        <h3 className="font-medium text-gray-800">
                          {transaction.type === 'sent' ? transaction.receiverName : transaction.senderName}
                        </h3>
                        <p className="text-sm text-gray-600">{transaction.country}</p>
                      </div>
                    </div>
                    
                    <div className="text-left">
                      <p className={`font-bold ${getTypeColor(transaction.type)}`}>
                        {transaction.type === 'sent' ? '-' : '+'}{transaction.amount} {transaction.currency}
                      </p>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(transaction.status)}`}>
                        {getStatusText(transaction.status)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-100 pt-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">التاريخ:</span>
                        <p className="font-medium">{new Date(transaction.date).toLocaleDateString('ar-SA')}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">العمولة:</span>
                        <p className="font-medium">{transaction.commission} {transaction.currency}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-500">رقم المرجع:</span>
                        <p className="font-medium text-xs">{transaction.reference}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* إحصائيات سريعة */}
          <div className="bg-white rounded-xl p-4 shadow-sm mt-6">
            <h3 className="font-semibold text-gray-800 mb-3">إحصائيات سريعة</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-blue-600">{transactions.length}</p>
                <p className="text-xs text-gray-600">إجمالي التحويلات</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">
                  {transactions.filter(t => t.status === 'completed').length}
                </p>
                <p className="text-xs text-gray-600">مكتملة</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-600">
                  {transactions.filter(t => t.status === 'pending').length}
                </p>
                <p className="text-xs text-gray-600">معلقة</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
